import { DEFAULT_USER_SETTINGS, isSameDate } from "@taskfortress/core"
import React, { useCallback, useMemo } from "react"
import { useData } from "../../providers/DataProvider"
import { Box } from "../Box"
import { Modal } from "../Modal"
import { Text } from "../Text"
import { Pressable } from "../gluestack/pressable"
import { DatePickerCore } from "./DatePickerCore"
import { type QuickDateOption, getQuickDateOptions } from "./quickDateOptions"

interface DatePickerDesktopProps {
  isVisible: boolean
  onClose: () => void
  selectedDate: string
  onDateSelect: (date: string) => void
  explicitSelectRequired?: boolean
  minDate?: string
  maxDate?: string
  disabledDates?: string[]
  showTaskIndicators?: boolean
}

export const DatePickerDesktop: React.FC<DatePickerDesktopProps> = ({
  isVisible,
  onClose,
  selectedDate,
  onDateSelect,
  explicitSelectRequired = false,
  minDate,
  maxDate,
  disabledDates,
  showTaskIndicators = false,
}) => {
  const { userSettings, tasks } = useData()
  const quickDateOptions = useMemo(() => getQuickDateOptions(), [])

  const handleDateSelect = useCallback(
    (date: string) => {
      onDateSelect(date)
      if (!explicitSelectRequired) {
        onClose()
      }
    },
    [onDateSelect, onClose, explicitSelectRequired],
  )

  const handleQuickDateSelect = useCallback(
    (option: QuickDateOption) => {
      handleDateSelect(option.date)
    },
    [handleDateSelect],
  )

  return (
    <Modal
      isVisible={isVisible}
      onClose={onClose}
      title="Select Date"
      contentClassName="z-50"
      closeOnBackdropPress={false}
    >
      <Box className="flex-row gap-4">
        {/* Quick Date Options - Horizontal Layout */}
        <Box className="w-40 space-y-2 py-2">
          {quickDateOptions.map((option) => {
            const isSelected = isSameDate(selectedDate, option.date)
            return (
              <Pressable
                key={option.label}
                onPress={() => handleQuickDateSelect(option)}
                className="flex-row items-center justify-between py-2 px-3 rounded-lg bg-background-50 border border-outline-200 hover:border-outline-600"
              >
                <Text
                  className={`text-sm font-medium ${
                    isSelected ? "text-primary-500" : "text-typography-900"
                  }`}
                >
                  {option.label}
                </Text>
                <Text
                  className={`text-xs ${
                    isSelected ? "text-primary-400" : "text-typography-600"
                  }`}
                >
                  {option.dayOfWeek}
                </Text>
              </Pressable>
            )
          })}
        </Box>

        {/* Calendar */}
        <Box className="flex-1">
          <DatePickerCore
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            minDate={minDate}
            maxDate={maxDate}
            disabledDates={disabledDates}
            showTaskIndicators={showTaskIndicators}
            tasks={tasks}
            firstDayOfWeek={
              userSettings.firstDayOfWeek ||
              DEFAULT_USER_SETTINGS.firstDayOfWeek!
            }
            className="shadow-none"
          />
        </Box>
      </Box>
    </Modal>
  )
}
